import React, { useEffect, useState } from 'react';
import { ReactComponent as CloseIcon } from 'assets/icons/close-line.svg';
import usePricingContext from 'contexts/pricing.context';
import { IngredientListType, IngredientType } from 'models/pricing.model';
import { ENUM_UNIT } from 'models/common.model';
import {
  Dropdown,
  DropdownItem,
  DropdownList,
  Input,
  Table,
  TableData,
  TableRow
} from '@cokitchen/cokitchen-components';
import { ArrowDownIcon } from 'cokitchen-icons';
import { weightMeasurement } from 'utils/constants';
import { formatNumberWithCommas } from 'utils/functions';
import { useLocation } from 'react-router-dom';
import ConversionItemsTable from './conversion-items-table';

const AddConversionListStoreItems = () => {
  const {
    conversionListFormData,
    setConversionListFormData,
    selectedConversionItem,
    setSelectedConversionItem
  } = usePricingContext();

  const location = useLocation();
  const { state, pathname } = location;
  const isPrepConfig = pathname.includes('excel-prep');
  const tableHeaders = !isPrepConfig
    ? ['', 'Ingredients', 'Price per unit', 'Quantity', 'Cost', '']
    : ['Ingredients', 'Price per unit', 'Quantity', 'Cost', ''];
  const isEdit = state;
  const [cost, setCost] = useState({
    foodCost: 0,
    packagingCost: 0,
    pricePerPortion: 0,
    totalCost: 0
  });

  const calculateTotalPrice = (items?: IngredientType[]): number => {
    if (!items) return 0;
    return items.reduce((total, item) => {
      const quantity = Number(item.quantity) || 0;
      const price = Number(item.ingredient?.min_vendor_price ?? item.min_vendor_price) || 0;
      return total + quantity * price;
    }, 0);
  };

  const addItems = (type: 'ingredients' | 'packaging') => {
    const newVendors = [...conversionListFormData];
    const newVendor = selectedConversionItem;
    if (!newVendor) return;
    newVendor[type] = [
      ...newVendor[type],
      { name: '', min_quantity: '', type: type === 'ingredients' ? 'meal-item' : type }
    ];
    setSelectedConversionItem(newVendor);
    setConversionListFormData(
      newVendors.map((item, index) => (index === newVendor?.itemIndex ? newVendor : item))
    );
  };

  const removeConversionItem = (index: number) => {
    if (conversionListFormData.length < 2) {
      return;
    }
    setConversionListFormData(conversionListFormData.filter((item, i) => index !== i));
  };

  const removeItems = (itemIndex: number, type: 'ingredients' | 'packaging') => {
    const newItems = [...conversionListFormData];
    const newItem = selectedConversionItem;

    if (!newItem || (type === 'ingredients' && newItem[type]?.length < 2)) return;
    newItem[type] = newItem[type].filter((_, idx) => idx !== itemIndex);
    setSelectedConversionItem(newItem);
    if (isEdit) {
      setConversionListFormData(
        newItems.map((item) => (item?.id === newItem?.id ? newItem : item))
      );
    } else {
      setConversionListFormData(
        newItems.map((item, index) => (index === newItem?.itemIndex ? newItem : item))
      );
    }
  };

  const handleInputChange = (
    itemIndex: number,
    value: string | number | ENUM_UNIT,
    name: keyof IngredientType,
    type: 'ingredients' | 'packaging',
    ingredient?: IngredientListType
  ) => {
    let newVendors = [...conversionListFormData];
    const newVendor = selectedConversionItem;
    if (!newVendor) return;
    const newItems = newVendor[type]?.map((b) => ({ ...b }));

    if (name === 'meal_type' && newItems[itemIndex]?.meal_type !== value) {
      newItems[itemIndex] = {
        ...newItems[itemIndex],
        meal_type: value as any,
        ingredient_name: '',
        name: '',
        ingredient: null,
        item_id: '',
        mix_id: '',
        min_vendor_price: '',
        unit_type: '',
        quantity: ''
      };
    } else {
      newItems[itemIndex][name] = value as any;
    }
    if (name == 'ingredient_name' || name === 'name') {
      newItems[itemIndex]['ingredient'] = ingredient;
      newItems[itemIndex]['mix_id'] = newVendor.id;
    }
    newVendor[type] = newItems;

    if (isEdit) {
      newVendors = newVendors.map((item) => (item?.id === newVendor.id ? newVendor : item));
    } else {
      newVendors = newVendors.map((_, i) => (i === newVendor.itemIndex ? newVendor : _));
    }

    setSelectedConversionItem(newVendor);
    setConversionListFormData(newVendors);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement> | { target: { name: string; value: string } },
    index: number
  ) => {
    const { name, value } = e.target;
    setConversionListFormData(
      conversionListFormData.map((itm, i) => (i === index ? { ...itm, [name]: value } : itm))
    );
    if (selectedConversionItem)
      setSelectedConversionItem({
        ...selectedConversionItem,
        [name]: value
      });
  };

  useEffect(() => {
    const foodCost = calculateTotalPrice(selectedConversionItem?.ingredients);
    const packagingCost = calculateTotalPrice(selectedConversionItem?.packaging);
    const totalCost = foodCost + packagingCost;
    const pricePerPortion = totalCost / Number(selectedConversionItem?.no_of_portions);

    setCost({
      foodCost,
      packagingCost,
      pricePerPortion,
      totalCost
    });
  }, [selectedConversionItem, conversionListFormData]);

  const costSummary = React.useMemo(
    () => [
      { name: 'Food cost', amount: cost.foodCost },
      { name: 'Packaging cost', amount: cost.packagingCost },
      { name: 'Total cost', amount: cost.totalCost },
      { name: 'Price per portion', amount: cost.pricePerPortion.toFixed(5) }
    ],
    [selectedConversionItem, cost]
  );
  return selectedConversionItem ? (
    <div>
      <div className='flex justify-start items-center gap-2 overflow-y-hidden overflow-x-auto w-full mb-10'>
        {conversionListFormData &&
          conversionListFormData.map((item, itemIndex) => {
            const isSelected = selectedConversionItem.itemIndex === itemIndex;
            return (
              <div
                onClick={() => {
                  setSelectedConversionItem({ ...item, itemIndex });
                }}
                className={`flex justify-between items-center gap-2 p-2 cursor-pointer min-w-[200px] max-w-[200px] ease-in-out duration-300 transition-colors border-b-2 ${
                  isSelected ? 'bg-[#FEF0F2] border-[#E60A2B]' : 'border-transparent'
                }`}
                key={itemIndex}>
                <div className='flex flex-col justify-start items-start gap-2'>
                  <span className='font-semibold'>{item.name}</span>
                  <span className='text-gray-9 text-sm'>{item.secton_name}</span>
                </div>

                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    removeConversionItem(itemIndex);
                  }}
                  className='min-w-[20px] min-h-[20px] max-w-[20px] max-h-[20px] flex justify-center items-center rounded-full hover:bg-[#FEDDE2] ease-in-out duration-300 transition-colors cursor-pointer'>
                  <CloseIcon width={30} />
                </span>
              </div>
            );
          })}
      </div>

      <div className='flex justify-start items-center gap-4'>
        <span className='font-semibold'>Number of portions</span>

        <div className='w-full md:w-[250px] relative'>
          <Input
            design='bordered'
            placeholder='number of portions'
            name='no_of_portions'
            value={selectedConversionItem.no_of_portions}
            onChange={(e) => handleChange(e, selectedConversionItem.itemIndex)}
          />
          <div className='absolute w-[50px] h-[20px] z-10 rounded-[2px] shadow bottom-[20%] right-[5%]'>
            <Dropdown
              hideOnClick
              content={
                <div className='h-5 p-1 text-[11px] flex justify-between items-center gap-[2px] cursor-pointer measurement_dropdown'>
                  <span className='text-[#6F7376] truncate'>
                    {selectedConversionItem.portion_type}
                  </span>
                  <ArrowDownIcon className='w-5 h-5 text-[#96999C]' />
                </div>
              }>
              <DropdownList>
                {weightMeasurement.map((item) => (
                  <DropdownItem
                    key={item.value}
                    onClick={() => {
                      handleChange(
                        { target: { name: 'portion_type', value: item.value } },
                        selectedConversionItem.itemIndex
                      );
                    }}>
                    {item.name}
                  </DropdownItem>
                ))}
              </DropdownList>
            </Dropdown>
          </div>
        </div>
      </div>

      <>
        <div className='w-full my-6 flex justify-start items-center bg-[#F4F5F5] p-2 gap-2 '>
          <span className='font-semibold text-base'>Food cost</span>
          <span className='text-sm text-gray-12 bg-gray-13 px-2 py-0.5 rounded-full'>
            ₦{formatNumberWithCommas(cost.foodCost)}
          </span>
        </div>
        <ConversionItemsTable
          items={selectedConversionItem?.ingredients || []}
          itemType='ingredients'
          tableHeaders={tableHeaders}
          isPrepConfig={isPrepConfig}
          onMealTypeChange={(index, value) => {
            handleInputChange(index, value, 'meal_type', 'ingredients');
          }}
          onInputChange={(itemIndex, value, name, type, ingredient) => {
            handleInputChange(itemIndex, value, name, type, ingredient);
          }}
          onRemoveItem={(itemIndex) => removeItems(itemIndex, 'ingredients')}
        />

        <button
          onClick={() => addItems('ingredients')}
          className='text-[#0080E5] font-medium  mt-3'>
          + Add Ingredient
        </button>
      </>

      {/* Packaging */}

      <>
        <div className='w-full my-6 flex justify-start items-center bg-[#F4F5F5] p-2 gap-2 '>
          <span className='font-semibold text-base'>Packaging cost</span>
          <span className='text-sm text-gray-12 bg-gray-13 px-2 py-0.5 rounded-full'>
            ₦{formatNumberWithCommas(cost.packagingCost)}
          </span>
        </div>
        <ConversionItemsTable
          items={selectedConversionItem?.packaging || []}
          itemType='packaging'
          tableHeaders={tableHeaders}
          isPrepConfig={isPrepConfig}
          onMealTypeChange={(index, value) => {
            handleInputChange(index, value, 'meal_type', 'packaging');
          }}
          onInputChange={(itemIndex, value, name, type, ingredient) => {
            handleInputChange(itemIndex, value, name, type, ingredient);
          }}
          onRemoveItem={(itemIndex) => removeItems(itemIndex, 'packaging')}
        />

        <button
          onClick={() => addItems('packaging')}
          className='text-[#0080E5] font-medium text-sm mt-1'>
          + Add Ingredient
        </button>
      </>

      <div className='flex flex-col justify-start items-start gap-4 w-full'>
        <div className='w-full mt-10 flex justify-start items-center bg-[#F4F5F5] p-2 gap-2 '>
          <span className='font-semibold text-base'>Cost Summary</span>
        </div>

        <Table headers={['Name', 'Amount', '', '', '']}>
          {costSummary?.map(({ name, amount }, itemIndex) => {
            return (
              <TableRow key={itemIndex}>
                <TableData>
                  <span className='text-sm'>{name}</span>
                </TableData>
                <TableData>
                  <span className='text-sm '>₦{formatNumberWithCommas(amount)}</span>
                </TableData>
              </TableRow>
            );
          })}
        </Table>
      </div>
    </div>
  ) : null;
};

export default AddConversionListStoreItems;
